import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_NOTES } from '../graphql/queries';
import { DELETE_NOTE } from '../graphql/mutations';
import NoteCard from './NoteCard';
import AddNoteModal from './AddNoteModal';
import { UserRoleLabels, NoteVisibilityRoleKeys } from '../utils/enums';

const NotesWidget = ({ isAdmin, userRole, isLoggingOut = false }) => {
  // Check if user can manage notes (create, edit, delete)
  const canManageNotes = isAdmin || userRole === 'HOUSEKEEPER_MANAGER' || userRole === 'PROPERTY_MANAGER';
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [notesToView, setNotesToView] = useState('current'); // 'current', 'upcoming', 'all'

  const { data: notesData, loading, error, refetch } = useQuery(GET_NOTES, {
    fetchPolicy: 'cache-and-network', // Use cache but always check network for updates
    notifyOnNetworkStatusChange: true, // This will update loading state when refetching
    skip: isLoggingOut // Only skip if user is logging out - let backend handle permissions
  });

  const [deleteNote, { loading: deleteLoading }] = useMutation(DELETE_NOTE, {
    onCompleted: () => {
      refetch();
    }
  });

  const handleDeleteNote = async (id) => {
    if (window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      try {
        await deleteNote({
          variables: { id }
        });
      } catch (err) {
        console.error('Error deleting note:', err);
      }
    }
  };

  const handleAddNoteSuccess = () => {
    setIsAddNoteModalOpen(false);
    // After adding a note, refetch with network-only to ensure we get fresh data
    refetch({ fetchPolicy: 'network-only' })
      .catch(err => {
        console.error('Error refetching notes:', err);
      });
  };

  // Filter notes based on user role and date criteria
  const filterNotes = () => {
    if (!notesData?.notes) {
      return [];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // First filter by role visibility
    const filteredByRole = notesData.notes.filter(note => {
      // Check if visibleToRoles is an array
      if (!Array.isArray(note.visibleToRoles)) {
        console.error('visibleToRoles is not an array for note:', note);
        return false;
      }

      // Admin and manager roles can see ALL notes regardless of role assignments
      if (isAdmin || userRole === 'HOUSEKEEPER_MANAGER' || userRole === 'PROPERTY_MANAGER') {
        return true;
      }

      // For non-admin users, check if their role is included
      // Convert all roles to uppercase for case-insensitive comparison
      const normalizedVisibleRoles = note.visibleToRoles.map(role =>
        typeof role === 'string' ? role.toUpperCase() : role);
      const normalizedUserRole = typeof userRole === 'string' ? userRole.toUpperCase() : userRole;

      // User can see notes visible to their role
      return normalizedVisibleRoles.includes(normalizedUserRole);
    });

    // Then filter by date based on user selection
    let dateFiltered;
    switch (notesToView) {
      case 'current':
        dateFiltered = filteredByRole.filter(note => {
          const startDate = note.startDate ? new Date(note.startDate) : null;
          const endDate = note.endDate ? new Date(note.endDate) : null;

          // If no dates are specified, the note is always current
          if (!startDate && !endDate) {
            return true;
          }

          // If only start date is specified, note is current if start date is in the past
          if (startDate && !endDate) {
            return startDate <= today;
          }

          // If only end date is specified, note is current if end date is in the future
          if (!startDate && endDate) {
            return endDate >= today;
          }

          // If both dates are specified, note is current if today is between them
          return startDate <= today && endDate >= today;
        });
        break;
      case 'upcoming':
        dateFiltered = filteredByRole.filter(note => {
          const startDate = note.startDate ? new Date(note.startDate) : null;
          return startDate && startDate > today;
        });
        break;
      case 'all':
      default:
        dateFiltered = filteredByRole;
    }

    return dateFiltered;
  };

  const sortedNotes = () => {
    return filterNotes().sort((a, b) =>
      new Date(b.createdAt) - new Date(a.createdAt)
    );
  };

  const notes = sortedNotes();

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
      <div className="px-6 py-4 bg-gradient-to-r from-sky-50 to-white border-b border-gray-200 lg:flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center pb-3 lg:pb-0">
          <svg className="w-5 h-5 mr-2 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Team Notes
        </h2>

        <div className="flex space-x-3">
          {/* Note filters */}
          <select
            value={notesToView}
            onChange={(e) => setNotesToView(e.target.value)}
            className="text-sm border border-gray-300 rounded-md py-1.5 px-3 bg-white shadow-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
            aria-label="Filter notes"
          >
            <option value="current">Current</option>
            <option value="upcoming">Upcoming</option>
            <option value="all">All Notes</option>
          </select>

          {/* Add Note button for managers */}
          {canManageNotes && (
            <button
              onClick={() => setIsAddNoteModalOpen(true)}
              className="bg-sky-600 text-white px-3 py-1.5 rounded-md hover:bg-sky-700 text-sm font-medium flex items-center transition-colors duration-150 shadow-sm"
              aria-label="Add note"
              tabIndex="0"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Note
            </button>
          )}
        </div>
      </div>

      <div className="p-2 lg:p-5">
        {loading ? (
          <div className="text-center py-10">
            <svg className="animate-spin h-8 w-8 mx-auto text-sky-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="mt-3 text-sm text-gray-600 font-medium">Loading notes...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-500">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <svg className="w-6 h-6 mx-auto text-red-500 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-600">Error loading notes: {error.message}</p>
            </div>
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded-lg border border-gray-100">
            <svg className="mx-auto h-14 w-14 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-3 text-gray-600 text-sm font-medium">No notes to display</p>
            {canManageNotes && (
              <button
                onClick={() => setIsAddNoteModalOpen(true)}
                className="mt-4 bg-sky-600 text-white px-4 py-2 rounded-md hover:bg-sky-700 text-sm font-medium inline-flex items-center shadow-sm transition-colors duration-150"
                aria-label="Create first note"
                tabIndex="0"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create First Note
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4 max-h-[450px] overflow-y-auto pr-1">
            {notes.map(note => (
              <NoteCard
                key={note.id}
                note={note}
                canManage={canManageNotes}
                userRole={userRole}
                onDelete={() => handleDeleteNote(note.id)}
                deleteLoading={deleteLoading}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Note Modal */}
      {isAddNoteModalOpen && (
        <AddNoteModal
          onClose={() => setIsAddNoteModalOpen(false)}
          onSuccess={handleAddNoteSuccess}
          availableRoles={NoteVisibilityRoleKeys}
          roleLabels={UserRoleLabels}
        />
      )}
    </div>
  );
};

export default NotesWidget;
